import React, { useState } from 'react';
import { X, DollarSign, Calendar } from 'lucide-react';

const FuelPricingModal = ({ apiCall, onClose, onSuccess, showError }) => {
  const [pricingMode, setPricingMode] = useState('single'); // 'single' or 'range'
  const [pricingData, setPricingData] = useState({
    fuelType: 'Gasoline 95',
    pricePerLiter: '',
    effectiveDate: new Date().toISOString().split('T')[0],
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date(new Date().setMonth(new Date().getMonth() + 1)).toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!pricingData.pricePerLiter || parseFloat(pricingData.pricePerLiter) <= 0) {
      showError('Please enter a valid price');
      return;
    }

    try {
      setLoading(true);
      
      if (pricingMode === 'single') {
        await apiCall('/gas/pricing', {
          method: 'POST',
          body: JSON.stringify({
            fuelType: pricingData.fuelType,
            pricePerLiter: parseFloat(pricingData.pricePerLiter),
            effectiveDate: pricingData.effectiveDate
          })
        });
      } else {
        // Bulk insert for date range
        await apiCall('/gas/pricing/bulk', {
          method: 'POST',
          body: JSON.stringify({
            fuelType: pricingData.fuelType,
            pricePerLiter: parseFloat(pricingData.pricePerLiter),
            startDate: pricingData.startDate,
            endDate: pricingData.endDate
          })
        });
      }
      
      onSuccess();
    } catch (error) {
      showError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCalendar = () => {
    onClose();
    window.dispatchEvent(new CustomEvent('openPricingCalendar'));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <DollarSign className="text-green-600" size={24} />
            Set Fuel Prices
          </h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Calendar View Button */}
          <button
            type="button"
            onClick={handleOpenCalendar}
            className="w-full py-3 px-4 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold rounded-lg flex items-center justify-center gap-2 shadow-md transition-all"
          >
            <Calendar size={18} />
            View/Edit Pricing Calendar
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">or set prices manually</span>
            </div>
          </div>

          {/* Pricing Mode Toggle */}
          <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
            <button
              type="button"
              onClick={() => setPricingMode('single')}
              className={`flex-1 py-2 px-4 rounded-md font-semibold transition-colors ${
                pricingMode === 'single'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
            >
              Single Day
            </button>
            <button
              type="button"
              onClick={() => setPricingMode('range')}
              className={`flex-1 py-2 px-4 rounded-md font-semibold transition-colors ${
                pricingMode === 'range'
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-200'
              }`}
            >
              Date Range
            </button>
          </div>

          {/* Fuel Type Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Fuel Type *
            </label>
            <select
              required
              value={pricingData.fuelType}
              onChange={(e) => setPricingData({...pricingData, fuelType: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="Gasoline 95">Gasoline 95</option>
              <option value="Gasoline 98">Gasoline 98</option>
              <option value="Diesel">Diesel</option>
            </select>
          </div>

          {/* Price Per Liter */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Price per Liter ($) *
            </label>
            <input
              type="number"
              step="0.001"
              min="0.001"
              required
              value={pricingData.pricePerLiter}
              onChange={(e) => setPricingData({...pricingData, pricePerLiter: e.target.value})}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 text-lg font-semibold"
              placeholder="0.000"
              autoFocus
            />
          </div>

          {/* Date Selection */}
          {pricingMode === 'single' ? (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Effective Date *
              </label>
              <input
                type="date"
                required
                value={pricingData.effectiveDate}
                onChange={(e) => setPricingData({...pricingData, effectiveDate: e.target.value})}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Start Date *
                </label>
                <input
                  type="date"
                  required
                  value={pricingData.startDate}
                  onChange={(e) => setPricingData({...pricingData, startDate: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  End Date *
                </label>
                <input
                  type="date"
                  required
                  value={pricingData.endDate}
                  onChange={(e) => setPricingData({...pricingData, endDate: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-800">
              {pricingMode === 'single' ? (
                <>
                  <strong>Single Day:</strong> Set price for {pricingData.fuelType} on{' '}
                  {new Date(pricingData.effectiveDate).toLocaleDateString()}
                </>
              ) : (
                <>
                  <strong>Date Range:</strong> Set price of ${pricingData.pricePerLiter || '0.000'}/L for{' '}
                  {pricingData.fuelType} from{' '}
                  {new Date(pricingData.startDate).toLocaleDateString()} to{' '}
                  {new Date(pricingData.endDate).toLocaleDateString()}{' '}
                  ({Math.ceil((new Date(pricingData.endDate) - new Date(pricingData.startDate)) / (1000 * 60 * 60 * 24)) + 1} days)
                </>
              )}
            </p>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4">
            <button 
              type="submit" 
              disabled={loading}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white font-semibold py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                'Setting Prices...'
              ) : (
                <>
                  <DollarSign size={18} />
                  {pricingMode === 'single' ? 'Set Price' : 'Set Prices for Range'}
                </>
              )}
            </button>
            <button 
              type="button" 
              onClick={onClose} 
              className="px-6 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FuelPricingModal;